import {
  STATUS_CFG,
  STATUS_ORDER,
  STATUS_PIPELINES,
  STATUS_BUCKETS,
  STATUS_LABELS,
  PIPELINE_COLORS,
  CLOSED_STATUSES,
  getPipeline,
  getStatusesForType,
  getStatusOrder,
  isClosedStatus,
  isCompletedStatus,
  isTerminalStatus,
  statusBucket,
  bucketStatuses,
} from './status-pipelines.js';

export {
  STATUS_CFG,
  STATUS_ORDER,
  STATUS_PIPELINES,
  STATUS_BUCKETS,
  STATUS_LABELS,
  PIPELINE_COLORS,
  CLOSED_STATUSES,
  getPipeline,
  getStatusesForType,
  getStatusOrder,
  isClosedStatus,
  isCompletedStatus,
  isTerminalStatus,
  statusBucket,
  bucketStatuses,
};

export const TZ = 'Asia/Colombo';

export const AVATAR_COLORS = ['#00AEEF', '#8A63D2', '#34d399', '#60a5fa', '#fb923c', '#f472b6', '#22d3ee', '#f87171'];

export function esc(s) {
  const el = document.createElement('div');
  el.textContent = s == null ? '' : String(s);
  return el.innerHTML;
}

export function getInitials(n) {
  const p = String(n || '').trim().split(/\s+/);
  if (!p[0]) return '?';
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : p[0].substring(0, 2).toUpperCase();
}

export function getAvatarColor(name) {
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d.length === 10 ? d + 'T12:00:00+05:30' : d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-LK', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ,
  });
}

export function fmtDateTime(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleString('en-LK', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: TZ,
  });
}

export function fmtAmt(a) {
  if (a == null || a === '') return '—';
  const n = Number(a);
  if (Number.isNaN(n)) return '—';
  return `LKR ${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export function fmtAmtParts(a) {
  if (a == null || a === '') return { currency: '', amount: '—' };
  const n = Number(a);
  if (Number.isNaN(n)) return { currency: '', amount: '—' };
  return { currency: 'LKR', amount: n.toLocaleString('en-US', { maximumFractionDigits: 0 }) };
}

export function leadOutstandingBalance(lead) {
  const bal = lead?.extra?.balance_due;
  if (bal == null || bal === '') return 0;
  const n = Number(bal);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function numOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function tourPaxCounts(extra = {}) {
  const finalSd = extra.stage_data?.final_quote_sent || {};
  const agentSd = extra.stage_data?.quote_received_agent || {};
  const pax = finalSd.pax || agentSd.pax || extra.pax || extra.tour_quote_request?.pax || {};
  return {
    adt: numOrNull(pax.adt) ?? 0,
    chd: numOrNull(pax.chd) ?? 0,
    inf: numOrNull(pax.inf) ?? 0,
  };
}

function ratesTotalByPax(rates, pax) {
  if (!rates || typeof rates !== 'object') return null;
  let total = 0;
  let hasValue = false;
  for (const key of ['adt', 'stu', 'chd', 'inf']) {
    const rate = numOrNull(rates[key]);
    const count = pax[key] ?? 0;
    if (rate != null && rate > 0 && count > 0) {
      total += rate * count;
      hasValue = true;
    }
  }
  return hasValue ? Math.round(total) : null;
}

/** Billable visa passengers (ADT + CHD; INF not billed). */
export function visaBillablePax(extra = {}) {
  const pax = extra.pax || {};
  const adt = numOrNull(pax.adt) ?? 0;
  const chd = numOrNull(pax.chd) ?? 0;
  return adt + chd || adt || 1;
}

export function visaCostTotal(extra, costPerAdt) {
  const cost = numOrNull(costPerAdt) ?? numOrNull(extra?.pax_cost_rates?.adt);
  if (cost == null || cost <= 0) return null;
  return Math.round(cost * visaBillablePax(extra));
}

/** Keep extra.pax_* rates aligned with visa sell/cost totals for accounting. */
export function syncVisaPricingExtra(extra = {}, { sellTotal, costPerAdt, costTotal } = {}) {
  const e = { ...extra };
  const billable = visaBillablePax(e);
  const pax = e.pax || { adt: 1, chd: 0 };

  if (sellTotal != null && sellTotal > 0) {
    const sellPerAdt = Math.round(Number(sellTotal) / billable);
    e.pax_rates = {
      adt: sellPerAdt,
      chd: (pax.chd || 0) > 0 ? sellPerAdt : null,
      inf: null,
    };
  }

  let cost = numOrNull(costPerAdt);
  if (cost == null && costTotal != null && costTotal > 0) {
    cost = Math.round(Number(costTotal) / billable);
  }
  if (cost == null) cost = numOrNull(e.pax_cost_rates?.adt);

  if (cost != null && cost >= 0) {
    cost = Math.round(cost);
    e.pax_cost_rates = {
      adt: cost,
      ...((pax.chd || 0) > 0 ? { chd: cost } : {}),
    };
    const sellPerAdt = numOrNull(e.pax_rates?.adt);
    if (sellPerAdt != null) {
      const profitPerAdt = sellPerAdt - cost;
      e.pax_profit_rates = {
        adt: profitPerAdt,
        ...((pax.chd || 0) > 0 ? { chd: profitPerAdt } : {}),
      };
    }
  }

  return e;
}

function ticketPaxCounts(extra = {}) {
  const pax = extra.pax || {};
  return {
    adt: numOrNull(pax.adt) ?? 0,
    stu: numOrNull(pax.stu) ?? 0,
    chd: numOrNull(pax.chd) ?? 0,
    inf: numOrNull(pax.inf) ?? 0,
  };
}

export function ticketPricingFromExtra(extra = {}) {
  const pax = ticketPaxCounts(extra);

  if (extra.adt_profiles?.length || extra.stu_profiles?.length) {
    let cost = 0;
    let profit = 0;
    let hasValue = false;
    const addTraveler = (profiles) => {
      if (!Array.isArray(profiles)) return;
      for (const pr of profiles) {
        const base = numOrNull(pr.cost) ?? 0;
        const typeProfit = numOrNull(pr.profit) ?? 0;
        if (base > 0 || typeProfit > 0) {
          cost += base;
          profit += typeProfit;
          hasValue = true;
        }
      }
    };
    addTraveler(extra.adt_profiles);
    addTraveler(extra.stu_profiles);
    const costs = extra.pax_cost_rates || {};
    const profits = extra.pax_profit_rates || {};
    for (const key of ['chd', 'inf']) {
      const count = pax[key] ?? 0;
      if (!count) continue;
      const base = numOrNull(costs[key]) ?? 0;
      const typeProfit = numOrNull(profits[key]) ?? 0;
      if (base > 0 || typeProfit > 0) {
        cost += count * base;
        profit += count * typeProfit;
        hasValue = true;
      }
    }
    if (!hasValue) return null;
    const roundedCost = Math.round(cost);
    const roundedProfit = Math.round(profit);
    return {
      cost: roundedCost,
      profit: roundedProfit,
      total: roundedCost + roundedProfit,
    };
  }

  const costs = extra.pax_cost_rates || {};
  const profits = extra.pax_profit_rates || {};
  let cost = 0;
  let profit = 0;
  let hasValue = false;
  for (const key of ['adt', 'stu', 'chd', 'inf']) {
    const count = pax[key] ?? 0;
    if (!count) continue;
    const base = numOrNull(costs[key]) ?? 0;
    const typeProfit = numOrNull(profits[key]) ?? 0;
    if (base > 0 || typeProfit > 0) {
      cost += count * base;
      profit += count * typeProfit;
      hasValue = true;
    }
  }
  if (!hasValue) return null;
  const roundedCost = Math.round(cost);
  const roundedProfit = Math.round(profit);
  return {
    cost: roundedCost,
    profit: roundedProfit,
    total: roundedCost + roundedProfit,
  };
}

export function ticketQuotedTotal(lead) {
  if (!lead) return null;
  const extra = lead.extra || {};
  const fromPax = ticketPricingFromExtra(extra);
  if (fromPax?.total != null) return fromPax.total;
  return numOrNull(lead.quote_amount) ?? numOrNull(lead.fare_amount) ?? numOrNull(extra.quoted_total);
}

function tourProfitMarkup(lead) {
  const extra = lead?.extra || {};
  const finalSd = extra.stage_data?.final_quote_sent || {};
  const components = finalSd.quote_components || extra.final_quote_components || {};
  const markup = finalSd.profit_markup ?? components.profit_markup ?? null;

  if (typeof markup === 'number' || (typeof markup === 'string' && markup !== '')) {
    const flat = numOrNull(markup);
    if (flat != null) return Math.round(flat);
  }

  return ratesTotalByPax(markup, tourPaxCounts(extra));
}

function tourQuotedTotal(lead, extra = {}) {
  return lead.amount
    ?? extra.quoted_total
    ?? extra.stage_data?.final_quote_sent?.total
    ?? extra.stage_data?.tour_accepted_advance?.quoted_total
    ?? null;
}

/** Keep tour agent-quote stage data aligned when cost is edited manually. */
export function syncTourAgentCost(extra = {}, costAmount) {
  const cost = numOrNull(costAmount);
  if (cost == null) return { ...extra };
  const out = { ...extra };
  const prev = out.stage_data?.quote_received_agent || {};
  out.stage_data = {
    ...(out.stage_data || {}),
    quote_received_agent: { ...prev, total: cost, agent_total: cost },
  };
  out.agent_quote = { ...(out.agent_quote || {}), total: cost };
  return out;
}

export function effectiveCostAmount(lead) {
  if (!lead) return null;
  const extra = lead.extra || {};
  if (lead.type === 'ticket' || lead.type === 'visa') {
    const fromPax = ticketPricingFromExtra(extra);
    if (fromPax?.cost != null) return fromPax.cost;
  }
  if (lead.type === 'tour') {
    if (lead.cost_amount != null) return Number(lead.cost_amount);
    const agent = extra.stage_data?.quote_received_agent;
    const total = agent?.total ?? extra.agent_quote?.total;
    if (total != null) return Number(total);
    return null;
  }
  if (lead.cost_amount != null) return Number(lead.cost_amount);
  return null;
}

export function effectiveSoldAmount(lead) {
  if (!lead) return null;
  const extra = lead.extra || {};
  const stored = lead.sold_amount != null ? Number(lead.sold_amount) : null;

  if (lead.type === 'ticket') {
    const quoted = ticketQuotedTotal(lead);
    const advance = extra.advance_paid != null ? Number(extra.advance_paid) : null;
    const balance = extra.balance_due != null ? Number(extra.balance_due) : null;

    if (extra.payment_complete || balance === 0 || lead.status === 'ticket_issued') {
      if (quoted != null) return Number(quoted);
      if (advance != null && stored != null) return advance + stored;
    }

    if (advance != null && stored != null) {
      const combined = advance + stored;
      if (quoted == null || combined <= Number(quoted)) return combined;
    }

    return stored;
  }

  if (lead.type === 'tour') {
    const quoted = tourQuotedTotal(lead, extra);
    const advance = extra.advance_paid != null ? Number(extra.advance_paid) : null;
    const balance = extra.balance_due != null ? Number(extra.balance_due) : null;
    const balanceCollected = extra.balance_collected_total != null ? Number(extra.balance_collected_total) : 0;
    const cash = (advance != null ? advance : 0) + balanceCollected;

    if (extra.payment_complete || balance === 0) {
      if (quoted != null) return Number(quoted);
      if (cash > 0) return cash;
      if (stored != null && stored > 0) return stored;
      return null;
    }

    if (balance != null && balance > 0) {
      if (cash > 0) return cash;
      if (stored != null && stored > 0) return stored;
      return null;
    }

    if (cash > 0) return cash;
    if (stored != null && stored > 0) return stored;
    return null;
  }

  if (lead.type === 'visa') {
    const quoted = lead.amount ?? stored;
    if (lead.status === 'visa_issued' && quoted != null) return Number(quoted);
    return stored ?? (quoted != null ? Number(quoted) : null);
  }

  return stored;
}

export function effectiveProfit(lead) {
  if (lead?.type === 'tour') return tourProfitMarkup(lead);
  const extra = lead?.extra || {};
  if (lead?.type === 'visa') {
    const stageQuote = numOrNull(extra.stage_data?.details_provided?.quote_price);
    if (stageQuote != null) {
      const sold = effectiveSoldAmount(lead);
      const cost = effectiveCostAmount(lead);
      if (sold != null && cost != null) return sold - cost;
    }
    const fromPax = ticketPricingFromExtra(extra);
    if (fromPax?.profit != null) return fromPax.profit;
  }
  if (lead?.type === 'ticket') {
    const fromPax = ticketPricingFromExtra(extra);
    if (fromPax?.profit != null) return fromPax.profit;
  }
  const sold = effectiveSoldAmount(lead);
  const cost = effectiveCostAmount(lead);
  if (sold == null || cost == null) return null;
  return sold - cost;
}

export function isOverdue(l) {
  if (!l || !l.followup_date) return false;
  if (isTerminalStatus(l.type, l.status)) return false;
  return l.followup_date < todayISO();
}

export function bookingExpiresMs(lead) {
  const raw = lead?.extra?.booking_expires;
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function isBookingExpired(lead) {
  if (!lead || lead.type !== 'ticket' || lead.status !== 'temporary_booking') return false;
  const ms = bookingExpiresMs(lead);
  return ms != null && ms <= Date.now();
}

export function isBookingExpiringSoon(lead, withinMs = 3600000) {
  if (!lead || lead.type !== 'ticket' || lead.status !== 'temporary_booking') return false;
  const ms = bookingExpiresMs(lead);
  if (ms == null || ms <= Date.now()) return false;
  return ms - Date.now() <= withinMs;
}

export function todayISO() {
  return new Date().toLocaleDateString('en-CA', { timeZone: TZ });
}

export function nowTime() {
  return new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ,
  });
}

export function colomboHour() {
  return Number(new Date().toLocaleString('en-US', {
    timeZone: TZ, hour: 'numeric', hour12: false,
  }));
}

/** Calendar month range in Asia/Colombo (matches worker overview defaults). */
export function colomboMonthRange(offsetMonths = 0) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: TZ });
  const [y, m] = today.split('-').map(Number);
  let year = y;
  let month = m + offsetMonths;
  while (month <= 0) { month += 12; year -= 1; }
  while (month > 12) { month -= 12; year += 1; }
  const monthStr = `${year}-${String(month).padStart(2, '0')}`;
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${monthStr}-01`,
    to: `${monthStr}-${String(lastDay).padStart(2, '0')}`,
    month: monthStr,
  };
}

export function colomboYearRange() {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: TZ });
  const year = today.slice(0, 4);
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export function whatsappUrl(phone) {
  let digits = String(phone || '').replace(/[^0-9]/g, '');
  if (!digits) return null;
  if (digits.startsWith('0') && digits.length === 10) digits = '94' + digits.slice(1);
  else if (digits.length === 9 && digits.startsWith('7')) digits = '94' + digits;
  return `https://wa.me/${digits}`;
}

export function showToast(msg, type = '') {
  const container = document.getElementById('toastContainer');
  if (!container) return;
  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  toast.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');
  toast.textContent = msg;
  container.appendChild(toast);
  setTimeout(() => toast.remove(), 3200);
}

const AGENT_PERM_DEFAULTS = {
  view_all: true,
  edit_all: false,
  takeover: false,
  delete: false,
  export: false,
  import: false,
  manage_agents: false,
  manage_settings: false,
  view_reports: true,
  view_accounting: false,
};

const VIEWER_PERM_DEFAULTS = {
  view_all: true,
  edit_all: false,
  delete: false,
  export: true,
  import: false,
  manage_agents: false,
  manage_settings: false,
  view_reports: true,
  view_accounting: true,
  view_only: true,
};

export function effectivePerms(agent) {
  if (!agent) return {};
  if (agent.role === 'admin') {
    return {
      view_all: true,
      edit_all: true,
      delete: true,
      export: true,
      import: true,
      manage_agents: true,
      manage_settings: true,
      view_reports: true,
      view_accounting: true,
    };
  }
  const base = isViewer(agent) ? VIEWER_PERM_DEFAULTS : AGENT_PERM_DEFAULTS;
  return { ...base, ...(agent.permissions || {}) };
}

export function hasPerm(agent, key) {
  if (!agent) return false;
  if (agent.role === 'admin') return true;
  return !!effectivePerms(agent)[key];
}

export function canMutate(agent) {
  return !!agent && !isViewer(agent);
}

export function canViewOwner(agent) {
  if (!agent) return false;
  if (agent.role === 'admin') return true;
  if (isViewer(agent)) return true;
  if (canMutate(agent)) return true;
  return hasPerm(agent, 'view_reports');
}

/** Agents may delete their own inquiries by default; deleting others' needs delete permission. */
export function canDeleteLead(agent, lead, access = null) {
  if (!agent || !lead) return false;
  if (agent.role === 'admin') return true;
  const canEdit = access?.can_edit ?? (
    hasPerm(agent, 'edit_all') || (canMutate(agent) && lead.agent_id === agent.id)
  );
  if (!canEdit) return false;
  const isOwner = access?.is_owner ?? lead.agent_id === agent.id;
  if (isOwner && canMutate(agent)) return true;
  return hasPerm(agent, 'delete') || hasPerm(agent, 'edit_all');
}

export function canViewAccounting(agent) {
  if (!agent) return false;
  if (agent.role === 'admin') return true;
  return hasPerm(agent, 'view_accounting');
}

/** Owner page entry — overview and/or ledger tab */
export function canAccessOwnerPage(agent) {
  return canViewOwner(agent) || canViewAccounting(agent);
}

/** Hide/show Overview nav link based on agent permissions. Ledger lives inside Overview. */
export function applyNavAccess(agent) {
  const showOwner = canAccessOwnerPage(agent);
  document.querySelectorAll('[data-nav-owner], #ownerLink').forEach((el) => {
    el.classList.toggle('hidden', !showOwner);
    el.hidden = !showOwner;
    if (showOwner && el.tagName === 'A') {
      el.textContent = canViewOwner(agent) ? 'Overview' : 'Ledger';
    }
  });
  document.querySelectorAll('[data-nav-accounting], #accountingLink').forEach((el) => {
    el.classList.add('hidden');
    el.hidden = true;
  });
}

export function isLiteModeOn() {
  return document.documentElement.classList.contains('lite-mode');
}

export function setLiteMode(on) {
  document.documentElement.classList.toggle('lite-mode', on);
  localStorage.setItem('mysky_lite_mode', on ? '1' : '0');
  if (on) {
    localStorage.setItem('mysky_visual_mode', 'lite');
    stripHeavyThemeAssets();
  } else {
    localStorage.setItem('mysky_visual_mode', 'full');
  }
  return on;
}

function stripHeavyThemeAssets() {
  document.documentElement.classList.remove('theme-pro', 'balanced-ui');
  delete document.documentElement.dataset.preview;
  document.getElementById('proPreviewBar')?.remove();
  document.getElementById('fontPreload')?.remove();
}

export function initLiteModeAssets() {
  if (isLiteModeOn()) stripHeavyThemeAssets();
}

export function isViewer(agent) {
  if (!agent) return false;
  return agent.role === 'viewer' || !!(agent.permissions && agent.permissions.view_only);
}

export function relativeTime(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return fmtDateTime(iso);
}

export function initPasswordToggles(root = document) {
  root.querySelectorAll('input[type="password"]').forEach((input) => {
    if (input.closest('.password-field')) return;

    const wrap = document.createElement('div');
    wrap.className = 'password-field';
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'password-toggle';
    btn.setAttribute('aria-label', 'Show password');
    btn.setAttribute('aria-pressed', 'false');
    btn.innerHTML = '<svg class="icon" aria-hidden="true"><use href="/icons.svg#eye"/></svg>';
    btn.addEventListener('click', () => {
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      btn.innerHTML = show
        ? '<svg class="icon" aria-hidden="true"><use href="/icons.svg#eye-slash"/></svg>'
        : '<svg class="icon" aria-hidden="true"><use href="/icons.svg#eye"/></svg>';
      btn.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
      btn.setAttribute('aria-pressed', show ? 'true' : 'false');
    });
    wrap.appendChild(btn);
  });
}

export function resetPasswordInput(input) {
  if (!input) return;
  input.value = '';
  input.type = 'password';
  const btn = input.closest('.password-field')?.querySelector('.password-toggle');
  if (btn) {
    btn.innerHTML = '<svg class="icon" aria-hidden="true"><use href="/icons.svg#eye"/></svg>';
    btn.setAttribute('aria-label', 'Show password');
    btn.setAttribute('aria-pressed', 'false');
  }
}

export function playReminderSound() {
  try {
    if (localStorage.getItem('mysky_sound') === '0') return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [523.25, 659.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02 + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35 + i * 0.12);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + 0.45 + i * 0.12);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch {
    /* autoplay blocked */
  }
}
